import { CaretLeft } from '@phosphor-icons/react';
import { initialen } from '../../../lib/immobilien/format';
import type { EigKopf, EigKopfZustand } from '../../../lib/eigentuemer/useEigentuemerKopf';
import { eigNav, type EigNavKey } from '../eigentuemerWidgets';
import { NavZeile } from './NavZeile';
import { SidebarDock } from './SidebarDock';

/** Badges laut README §6.6: Objekte, Mieter, Freigaben (warn), Termine (zukünftig), Dokumente, Notizen, Beteiligte. */
function anzahl(kopf: EigKopf | null, key: EigNavKey): number | null {
  if (!kopf) return null;
  switch (key) {
    case 'objekte': return kopf.anzahl.objekte;
    case 'mieter': return kopf.anzahl.mieter;
    case 'termine': return kopf.anzahl.termine;
    case 'dokumente': return kopf.anzahl.dokumente;
    case 'notizen': return kopf.anzahl.notizen;
    case 'beteiligte': return kopf.anzahl.beteiligte;
    default: return null;
  }
}

/** Kontext-Sidebar im Eigentümermodus (Handoff Eigentümer §6.6, Screens 10–14). */
export function EigentuemerModus({ eigentuemerId, zustand, panel, onNavigate }: {
  eigentuemerId: string;
  zustand: EigKopfZustand;
  /** ?panel=<key> der aktuellen URL */
  panel: string | null;
  onNavigate: (pfad: string) => void;
}) {
  const basis = `/admin/eigentuemer/${encodeURIComponent(eigentuemerId)}`;
  const kopf = zustand.status === 'ok' ? zustand.kopf : null;
  // Bis die Rolle geladen ist, nur die immer sichtbaren Abschnitte
  const nav = eigNav(kopf?.werte ?? { verwaltet: false, hasWEG: false, extern: false, hasMV: false, hasSEV: false });

  return (
    <div className="immo-side-in flex flex-col flex-1 min-h-0" style={{ gap: 12 }}>
      <button
        type="button"
        onClick={() => onNavigate('/admin/eigentuemer')}
        className="immo-hover-flaeche flex items-center self-start cursor-pointer"
        style={{ height: 30, padding: '0 12px 0 8px', borderRadius: 15, border: 'none', gap: 5, background: 'var(--fill2)', fontSize: 12, fontWeight: 700, color: 'var(--ink)' }}
      >
        <CaretLeft weight="bold" size={12} />
        Alle Eigentümer
      </button>

      <button
        type="button"
        onClick={() => onNavigate(basis)}
        aria-label={kopf ? `${kopf.name} – Übersicht` : 'Eigentümer-Übersicht'}
        className="immo-innen flex items-center cursor-pointer text-left"
        style={{ borderRadius: 18, border: 'none', padding: 12, gap: 10, color: 'var(--ink)' }}
      >
        <span className="immo-akzent flex items-center justify-center shrink-0" style={{ width: 42, height: 42, borderRadius: '50%', fontSize: 14, fontWeight: 800 }}>
          {kopf ? initialen(kopf.name) : ''}
        </span>
        <span className="flex flex-col min-w-0" style={{ gap: 1 }}>
          {zustand.status === 'laedt' && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Lädt …</span>}
          {zustand.status === 'fehler' && <span style={{ fontSize: 12, color: 'var(--bad)' }}>Eigentümer konnte nicht geladen werden.</span>}
          {zustand.status === 'fehlt' && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Eigentümer nicht gefunden.</span>}
          {kopf && (
            <>
              <span className="truncate" style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em' }}>{kopf.name}</span>
              <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{kopf.einheiten === 1 ? '1 Einheit' : `${kopf.einheiten} Einheiten`}</span>
            </>
          )}
        </span>
      </button>

      <nav aria-label="Eigentümernavigation" className="immo-ohne-scrollbar flex flex-col flex-1 min-h-0 overflow-auto" style={{ gap: 1, margin: '0 -4px', padding: '0 4px' }}>
        {nav.map(abschnitt => (
          <div key={abschnitt.abschnitt} className="flex flex-col" style={{ gap: 1 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink3)', padding: '12px 9px 4px' }}>{abschnitt.abschnitt}</span>
            {abschnitt.eintraege.map(e => {
              // offene Freigaben als Warn-Badge (README §6.6)
              const offen = e.key === 'freigaben' ? kopf?.anzahl.freigaben ?? null : null;
              return (
                <NavZeile
                  key={e.key}
                  art="objekt"
                  label={e.label}
                  icon={e.icon}
                  aktiv={e.key === 'uebersicht' ? !panel : panel === e.key}
                  anzahl={e.key === 'freigaben' ? null : anzahl(kopf, e.key)}
                  rechts={offen ? (
                    <span className="immo-zahl flex items-center justify-center" style={{ minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: 'var(--warn)', color: '#fff' }}>{offen}</span>
                  ) : undefined}
                  onClick={() => onNavigate(e.key === 'uebersicht' ? basis : `${basis}?panel=${e.key}`)}
                />
              );
            })}
          </div>
        ))}
      </nav>

      <SidebarDock aktiv="eigentuemer" onNavigate={onNavigate} />
    </div>
  );
}
